import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Container, Row } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle, FaClipboardList, FaRedo, FaSpinner
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AuditorAuditProgress from '../../../Components/AuditorAuditProgress';

function InAuditorDashboard() {
  const [stats, setStats] = useState({
    pendingChecklists: 0,
    inProgressChecklists: 0,
    completedChecklists: 0,
    reauditChecklists: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditor, setAuditor] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const auditorData = JSON.parse(localStorage.getItem('auditor'));
        setAuditor(auditorData);
        
        if (!auditorData || !auditorData.id || !auditorData.BranchCode) {
          throw new Error('Auditor data not found');
        }

        if (auditorData.id) {
          try {
            const profileResponse = await fetch(`${BASE_URL}/get-auditor/${auditorData.id}`);
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              if (profileData.profileImage) {
                setProfileImage(`${BASE_URL}${profileData.profileImage}`);
              }
            }
          } catch (profileError) {
            console.error("Failed to load profile image:", profileError);
          }
        }
        
        const response = await fetch(
          `${BASE_URL}/get-auditor-dashboard-stats/${auditorData.id}/${auditorData.BranchCode}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard statistics');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setStats(data.stats);
        } else {
          throw new Error(data.error || 'Failed to fetch dashboard statistics');
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [BASE_URL]);

  const totalChecklists = stats.pendingChecklists + stats.inProgressChecklists + stats.completedChecklists;

  const pendingPercentage = totalChecklists ? Math.round((stats.pendingChecklists / totalChecklists) * 100) : 0;
  const inProgressPercentage = totalChecklists ? Math.round((stats.inProgressChecklists / totalChecklists) * 100) : 0;
  const completedPercentage = totalChecklists ? Math.round((stats.completedChecklists / totalChecklists) * 100) : 0;

  const getAuditorInitials = () => {
    if (!auditor) return "AU";
    return `${auditor.firstName?.charAt(0) || ''}${auditor.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const handleStatCardClick = (status) => {
    navigate('/AuditChecklist', { state: { activeTab: status } });
  };

  const handleReauditCardClick = () => {
    navigate('/ReAuditChecklist', { state: { activeTab: 'pending' } });
  };

  const handleAuditOverviewClick = (tabType) => {
    navigate('/AuditOverview', { state: { activeTab: tabType } });
  };
  
  return (
    <Container fluid className="dashboard-container p-0">
      {/* Integrated header content without the styled header section */}
      <Container fluid className="px-4 pt-4 pb-2">
        <Row className="align-items-center mb-3">
          <Col md={6} className="mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              {profileImage ? (
                <div className="auditor-avatar-simple me-3">
                  <img 
                    src={profileImage} 
                    alt={`${auditor?.firstName} ${auditor?.lastName}`}
                    className="profile-image"
                  />
                </div>
              ) : (
                <div className="auditor-avatar-simple me-3">
                  {getAuditorInitials()}
                </div>
              )}
              <div>
                <h4 className="greeting mb-0 text-primary">Welcome {auditor?.firstName || 'Auditor'} {auditor?.lastName || ''}</h4>
                <p className="mb-0 text-muted">
                  <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      
      <Container fluid className="dashboard-content px-4 pb-3">
        {error && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch your stats...</p>
          </div>
        ) : (
          <div className="dashboard-grid">
            <div className="dashboard-section stats-overview">
              <h5 className="section-title mb-3">
                <FaChartLine className="me-2" /> Audit Statistics
              </h5>
              <div className="stats-cards">
                <Card className="stat-card clickable-card" onClick={() => handleStatCardClick('pending')}>
                  <Card.Body>
                    <div className="stat-icon pending-icon">
                      <FaClipboardList />
                    </div>
                    <div className="stat-content">
                      <h4 className="stat-value">{stats.pendingChecklists}</h4>
                      <p className="stat-label">Plan Audits</p>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card className="stat-card clickable-card" onClick={() => handleStatCardClick('in progress')}>
                  <Card.Body>
                    <div className="stat-icon progress-icon">
                      <FaSpinner />
                    </div>
                    <div className="stat-content">
                      <h4 className="stat-value">{stats.inProgressChecklists}</h4>
                      <p className="stat-label">In Progress</p>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card className="stat-card clickable-card" onClick={() => handleStatCardClick('completed')}>
                  <Card.Body>
                    <div className="stat-icon completed-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="stat-content">
                      <h4 className="stat-value">{stats.completedChecklists}</h4>
                      <p className="stat-label">Completed</p>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card className="stat-card clickable-card" onClick={handleReauditCardClick}>
                  <Card.Body>
                    <div className="stat-icon reaudit-icon">
                      <FaRedo />
                    </div>
                    <div className="stat-content">
                      <h4 className="stat-value">{stats.reauditChecklists}</h4>
                      <p className="stat-label">Re-audits</p>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="dashboard-section completion-rates">
              <h5 className="section-title mb-3">
                <FaChartLine className="me-2" /> Completion Rates
              </h5>
              <div className="completion-cards">
                <Card className="completion-card">
                  <Card.Body>
                    <div className="completion-content">
                      <div className="completion-header">
                        <span className="completion-label">Plan</span>
                        <span className="completion-percentage pending-text">{pendingPercentage}%</span>
                      </div>
                      <div className="completion-bar">
                        <div 
                          className="completion-fill pending-fill" 
                          style={{ width: `${pendingPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card className="completion-card">
                  <Card.Body>
                    <div className="completion-content">
                      <div className="completion-header">
                        <span className="completion-label">In Progress</span>
                        <span className="completion-percentage progress-text">{inProgressPercentage}%</span>
                      </div>
                      <div className="completion-bar">
                        <div 
                          className="completion-fill progress-fill" 
                          style={{ width: `${inProgressPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card className="completion-card">
                  <Card.Body>
                    <div className="completion-content">
                      <div className="completion-header">
                        <span className="completion-label">Completed</span>
                        <span className="completion-percentage completed-text">{completedPercentage}%</span>
                      </div>
                      <div className="completion-bar">
                        <div 
                          className="completion-fill completed-fill" 
                          style={{ width: `${completedPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
            <div className="dashboard-section actions-section">
              <h5 className="section-title mb-3">
                <FaClipboardList className="me-2" /> Quick Actions
              </h5>
              <div className="quick-actions">
                <div 
                  className="action-card audit-action"
                  onClick={() => handleAuditOverviewClick('audit')}
                  role="button"
                >
                  <div className="action-icon">
                    <FaClipboardList />
                  </div>
                  <span>Audit Checklists</span>
                </div>
                
                <div 
                  className="action-card reaudit-action"
                  onClick={() => handleAuditOverviewClick('reaudit')}
                  role="button"
                >
                  <div className="action-icon">
                    <FaRedo />
                  </div>
                  <span>Re-Audit Checklists</span>
                </div>
              </div>
            </div>
            </div>

            

            <div className="dashboard-section progress-section">
              <AuditorAuditProgress />
            </div>

           
          </div>
        )}
      </Container>

      <style>
        {`
          .dashboard-container {
            background-color: #f8f9fa;
            min-height: 100vh;
          }
          
          /* Updated styles for non-header design */
          .auditor-avatar-simple {
            width: 45px;
            height: 45px;
            background-color: rgba(var(--bs-primary-rgb), 0.1);
            border: 2px solid rgba(var(--bs-primary-rgb), 0.2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            color: var(--bs-primary);
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          
          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .greeting {
            font-weight: 600;
            font-size: 1.25rem;
          }
          
          .dashboard-content {
            padding-bottom: 30px;
          }
          
          .dashboard-grid {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            gap: 15px;
          }
          
          .dashboard-section {
            background-color: transparent;
            border-radius: 12px;
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
            font-size: 1rem;
          }
          
          .stats-overview {
            margin-bottom: 5px;
          }
          
          .stats-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 15px;
          }
          
          .stat-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            overflow: hidden;
            height: 100%;
          }
          
          .stat-card .card-body {
            padding: 15px;
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100%;
          }
          
          .stat-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .stat-content {
            flex-grow: 1;
          }
          
          .stat-value {
            font-weight: 700;
            font-size: 1.5rem;
            margin-bottom: 2px;
            line-height: 1.2;
          }
          
          .stat-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6c757d;
            margin: 0;
          }
          
          .completion-rates {
            margin-bottom: 5px;
            margin-top: 25px;
          }
          
          .completion-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
          }
          
          .completion-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            overflow: hidden;
            height: 100%;
          }
          
          .completion-card .card-body {
            padding: 15px;
          }
          
          .completion-content {
            width: 100%;
          }
          
          .completion-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }
          
          .completion-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6c757d;
          }
          
          .completion-percentage {
            font-size: 0.8rem;
            font-weight: 600;
          }
          
          .pending-text {
            color: #ffc107;
          }
          
          .progress-text {
            color: #0d6efd;
          }
          
          .completed-text {
            color: #198754;
          }
          
          .completion-bar {
            width: 100%;
            height: 6px;
            background-color: #e9ecef;
            border-radius: 3px;
            overflow: hidden;
          }
          
          .completion-fill {
            height: 100%;
            border-radius: 3px;
            transition: width 0.3s ease;
          }
          
          .pending-fill {
            background-color: #ffc107;
          }
          
          .progress-fill {
            background-color: #0d6efd;
          }
          
          .completed-fill {
            background-color: #198754;
          }
          
          .progress-section {
            margin-bottom: 5px;
          }
          
          .actions-section {
            margin-bottom: 5px;
             margin-top: 25px;
          }
          
          .quick-actions {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
          }
          
          .action-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 15px 10px;
            border-radius: 12px;
            background-color: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            text-decoration: none;
            color: #344767;
            cursor: pointer;
          }
          
          .action-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-bottom: 8px;
          }
          
          .action-card span {
            font-weight: 500;
            font-size: 0.8rem;
          }
          
          .pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .completed-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .reaudit-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .audit-action .action-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .reaudit-action .action-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .clickable-card {
            cursor: pointer;
            user-select: none;
          }
          
          .stat-card:hover, .action-card:hover, .clickable-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }

          .completion-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }
          
          @media (min-width: 992px) {
            .dashboard-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-template-areas:
                "stats progress"
                "completion progress"
                "actions progress";
              gap: 20px;
              align-items: start;
            }
            
            .stats-overview {
              grid-area: stats;
            }
            
            .completion-rates {
              grid-area: completion;
            }
            
            .progress-section {
              grid-area: progress;
              height: 100%;
              display: flex;
              flex-direction: column;
            }
            
            .actions-section {
              grid-area: actions;
            }
            
            .stats-cards {
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }
            
            .completion-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            
            .stat-card .card-body {
              padding: 12px;
            }
            
            .action-card {
              padding: 12px 8px;
            }
            
            .stat-value {
              font-size: 1.25rem;
            }
            
            .stat-icon {
              width: 35px;
              height: 35px;
              font-size: 16px;
            }
            
            .action-icon {
              width: 35px;
              height: 35px;
              font-size: 16px;
            }
            
            .action-card span {
              font-size: 0.75rem;
            }
            
            .completion-card .card-body {
              padding: 12px;
            }
            
            .completion-bar {
              height: 5px;
            }
          }
          
          @media (min-width: 1200px) {
            .dashboard-grid {
              grid-template-columns: 2fr 3fr;
              gap: 25px;
            }
            
            .stats-cards {
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            
            .completion-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            
            .stat-card .card-body {
              padding: 15px;
            }
            
            .action-card {
              padding: 15px 10px;
            }
            
            .stat-value {
              font-size: 1.5rem;
            }
            
            .stat-icon, .action-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }
            
            .action-card span {
              font-size: 0.8rem;
            }
            
            .completion-card .card-body {
              padding: 15px;
            }
            
            .completion-bar {
              height: 6px;
            }
          }
          
          @media (min-width: 1400px) {
            .dashboard-grid {
              grid-template-columns: 1fr 2fr;
            }
          }
          
          @media (max-width: 991px) {
            .dashboard-grid {
              grid-template-columns: 1fr;
              grid-template-areas:
                "stats"
                "completion"
                "progress"
                "actions";
            }
            
            .stats-overview {
              grid-area: stats;
            }
            
            .completion-rates {
              grid-area: completion;
            }
            
            .progress-section {
              grid-area: progress;
            }
            
            .actions-section {
              grid-area: actions;
            }
          }
          
          @media (max-width: 576px) {
            .greeting {
              font-size: 1.1rem;
            }
            
            .quick-actions {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .stat-value {
              font-size: 1.25rem;
            }
            
            .completion-cards {
              grid-template-columns: repeat(1, 1fr);
            }
            
            .completion-bar {
              height: 5px;
            }
          }
        `}
      </style>
    </Container>
  );
}

export default InAuditorDashboard;
